/**
 * Clears all app caches and unregisters service workers to ensure fresh content.
 * This helps prevent stale cached pages from appearing after code updates.
 */
export async function clearAppCache(): Promise<void> {
  try {
    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log(`[Cache] Clearing cache: ${cacheName}`);
          return caches.delete(cacheName);
        })
      );
      console.log('[Cache] All caches cleared');
    }

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => {
          console.log('[SW] Unregistering service worker');
          return registration.unregister();
        })
      );
      console.log('[SW] All service workers unregistered');
    }
  } catch (error) {
    console.error('[Cache] Error clearing caches:', error);
  }
}

// ============================================================================
// Runtime recovery for "stuck on Loading..." due to chunk/asset 404s
// (common after deployments when a service worker serves an old index.html)
// ============================================================================

const RECOVERY_FLAG_KEY = 'propertyshield_runtime_recovery_attempted';

const isChunkLoadError = (reason: unknown): boolean => {
  const message =
    typeof reason === 'string'
      ? reason
      : reason && typeof reason === 'object' && 'message' in reason
        ? String((reason as any).message)
        : '';

  // Vite / browser patterns when dynamic import fails due to 404/HTML response
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unexpected token \'<\'')
  );
};

/**
 * Adds global listeners that recover from stale cached assets by clearing caches
 * and reloading once.
 */
export function setupRuntimeChunkRecovery(): void {
  if (typeof window === 'undefined') return;

  const attemptRecovery = async (reason: unknown) => {
    if (!isChunkLoadError(reason)) return;
    if (sessionStorage.getItem(RECOVERY_FLAG_KEY) === '1') return;

    sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
    console.warn('[Recovery] Detected chunk load failure. Clearing caches and reloading...', reason);
    await clearAppCache();
    window.location.reload();
  };

  window.addEventListener('unhandledrejection', (event) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    attemptRecovery((event as PromiseRejectionEvent).reason);
  });

  window.addEventListener('error', (event) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    attemptRecovery((event as ErrorEvent).error || (event as ErrorEvent).message);
  });
}

/**
 * Checks if the app version has changed and clears cache if needed.
 * Uses localStorage to track the last known version.
 * 
 * Version History:
 * - v4.4.0-security-hardening: Critical RLS fixes for messaging + 4 warn-level table hardening
 * - v4.3.0-legacy-847-purge-complete: All hardcoded 847 values replaced, theme-aware styling applied
 * - v3.0.0-phase8-consolidation: Final Stanford Engineering Standards phase
 */
const APP_VERSION_KEY = 'propertyshield_app_version';
const CURRENT_VERSION = 'v4.8.0-ops-maturity-toggles'; // Forces cache/SW reset so updated ops maturity toggles appear

export async function checkAndClearCacheIfNeeded(): Promise<boolean> {
  try {
    const lastVersion = localStorage.getItem(APP_VERSION_KEY);
    
    if (lastVersion !== CURRENT_VERSION) {
      console.log(`[Cache] Version mismatch: ${lastVersion} -> ${CURRENT_VERSION}. Clearing cache...`);
      await clearAppCache();
      localStorage.setItem(APP_VERSION_KEY, CURRENT_VERSION);
      return true; // Cache was cleared
    }
    
    return false; // No cache clearing needed
  } catch (error) {
    console.error('[Cache] Error checking version:', error);
    return false;
  }
}
